import {
  Form,
  Input,
  Select,
  Typography,
  Card,
  Row,
  Col,
  Space,
  Checkbox,
  Modal,
  Tooltip,
  Alert,
  message,
} from "antd";
import {
  UserOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  WechatOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { useCompany } from "../../../hooks/useCompany";
import { supabase } from "../../../lib/supabase";
import { WizardStep } from "../WizardStep";
import {
  useCompanySuppliers,
  CompanySupplier,
  CompanySupplierInput,
} from "../../../hooks/useCompanySuppliers";
import { useTranslation } from "react-i18next";

interface SupplierFormValues {
  name: string;
  email: string;
  phone: string;
  wechat?: string;
  country: string;
  province: string;
  city: string;
  address: string;
  saveForLater: boolean;
}

export const SupplierDetails = () => {
  const [form] = Form.useForm<SupplierFormValues>();
  const { state, updateStep } = useBookingWizard();
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const {
    suppliers: companySuppliers,
    saveCompanySupplier,
    updateCompanySupplier,
    deleteCompanySupplier,
  } = useCompanySuppliers(selectedCompany);
  const [selectedCompanySupplier, setSelectedCompanySupplier] =
    useState<CompanySupplier | null>(null);
  const [formChanged, setFormChanged] = useState(false);
  const [initialValues, setInitialValues] = useState<SupplierFormValues | null>(
    null
  );
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [supplierToDelete, setSupplierToDelete] =
    useState<CompanySupplier | null>(null);

  // Check if the selected incoterm is EXW (ID: 1)
  const isExwIncoterm = state.booking_incoterm === 1;

  // Track form changes
  const handleFormChange = () => {
    if (!initialValues) return;

    const currentValues = form.getFieldsValue();
    const hasChanges = Object.keys(initialValues).some((key) => {
      // Skip the saveForLater field
      if (key === "saveForLater") return false;
      return (
        initialValues[key as keyof SupplierFormValues] !==
        currentValues[key as keyof SupplierFormValues]
      );
    });

    setFormChanged(hasChanges);
  };

  useEffect(() => {
    // If we have a supplier_id, fetch the booking supplier
    if (state.supplier_id) {
      const fetchBookingSupplier = async () => {
        const { data, error } = await supabase
          .from("booking_supplier")
          .select("*")
          .eq("id", state.supplier_id)
          .single();

        if (!error && data) {
          const formValues: SupplierFormValues = {
            name: data.name,
            email: data.email,
            phone: data.phone,
            wechat: data.wechat || undefined,
            country: data.country,
            province: data.province,
            city: data.city,
            address: data.address,
            saveForLater: false,
          };

          form.setFieldsValue(formValues);
          setInitialValues(formValues);

          // Check if this supplier matches a company supplier
          const matchingSupplier = companySuppliers.find(
            (s) =>
              s.name === data.name &&
              s.email === data.email &&
              s.phone === data.phone
          );

          if (matchingSupplier) {
            setSelectedCompanySupplier(matchingSupplier);
          }
        }
      };

      fetchBookingSupplier();
    }
  }, [state.supplier_id, form, companySuppliers]);

  const handleCompanySupplierSelect = (value: number | undefined) => {
    if (!value) {
      setSelectedCompanySupplier(null);
      form.resetFields();
      setInitialValues(null);
      return;
    }

    const supplier = companySuppliers.find((s) => s.id === value);
    if (supplier) {
      setSelectedCompanySupplier(supplier);

      const formValues: SupplierFormValues = {
        name: supplier.name,
        email: supplier.email,
        phone: supplier.phone,
        wechat: supplier.wechat || undefined,
        country: supplier.country,
        province: supplier.province,
        city: supplier.city,
        address: supplier.address,
        saveForLater: false,
      };

      form.setFieldsValue(formValues);
      setInitialValues(formValues);
    }
  };

  const handleDeleteSupplier = (
    supplier: CompanySupplier,
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation(); // Prevent the select dropdown from opening/closing
    setSupplierToDelete(supplier);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;

    try {
      await deleteCompanySupplier(supplierToDelete.id);
      message.success(
        t("bookingWizard.supplierDetails.supplierDeletedSuccess")
      );

      // If the deleted supplier was selected, reset the form
      if (selectedCompanySupplier?.id === supplierToDelete.id) {
        setSelectedCompanySupplier(null);
        form.resetFields();
        setInitialValues(null);
      }
    } catch (error) {
      console.error("Error deleting supplier:", error);
      message.error(t("bookingWizard.supplierDetails.supplierDeleteError"));
    } finally {
      setDeleteModalVisible(false);
      setSupplierToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setSupplierToDelete(null);
  };

  const onFinish = async (values: SupplierFormValues) => {
    try {
      let bookingSupplierId: number | undefined = state.supplier_id as
        | number
        | undefined;

      // Handle saving to company_supplier if checkbox is checked
      if (values.saveForLater && selectedCompany) {
        const companySupplierData: CompanySupplierInput = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          wechat: values.wechat,
          country: values.country,
          province: values.province,
          city: values.city,
          address: values.address,
          company_id: selectedCompany,
        };

        // If we're updating an existing company supplier
        if (selectedCompanySupplier) {
          await updateCompanySupplier(
            selectedCompanySupplier.id,
            companySupplierData
          );
        }
        // If we're creating a new company supplier
        else {
          await saveCompanySupplier(companySupplierData);
        }
      }

      // Always create or update a booking supplier
      if (selectedCompany) {
        const supplierData = {
          company_id: selectedCompany,
          name: values.name,
          email: values.email,
          phone: values.phone,
          wechat: values.wechat,
          country: values.country,
          province: values.province,
          city: values.city,
          address: values.address,
        };

        // If we already have a booking supplier, update it
        if (bookingSupplierId) {
          const { data: updatedSupplier, error } = await supabase
            .from("booking_supplier")
            .update(supplierData)
            .eq("id", bookingSupplierId)
            .select()
            .single();

          if (error) throw error;
          if (updatedSupplier) {
            bookingSupplierId = updatedSupplier.id;
          }
        }
        // Otherwise create a new one
        else {
          const { data: newSupplier, error } = await supabase
            .from("booking_supplier")
            .insert(supplierData)
            .select()
            .single();

          if (error) throw error;
          if (newSupplier) {
            bookingSupplierId = newSupplier.id;
          }
        }
      }

      // Update booking with supplier ID only
      await updateStep(2, {
        supplier_id: bookingSupplierId || null,
      });
    } catch (error) {
      console.error("Error saving supplier:", error);
    }
  };

  return (
    <WizardStep
      onNext={async () => {
        await form.submit();
      }}
      onPrevious={() => updateStep(state.currentStep - 1, {})}
    >
      <Typography.Title level={5} style={{ marginBottom: 16 }}>
        {t("bookingWizard.supplierDetails.title")}
      </Typography.Title>
      <Typography.Text
        type="secondary"
        style={{ marginBottom: 16, display: "block" }}
      >
        {t("bookingWizard.supplierDetails.selectSupplier")}
      </Typography.Text>

      <Select
        style={{ width: "100%", marginBottom: 24 }}
        placeholder={t("bookingWizard.supplierDetails.selectSupplier")}
        onChange={handleCompanySupplierSelect}
        value={selectedCompanySupplier?.id}
        size="large"
        allowClear
        optionLabelProp="label"
        dropdownRender={(menu) => <div>{menu}</div>}
        notFoundContent={t("bookingWizard.supplierDetails.noSavedSuppliers")}
      >
        {companySuppliers.map((supplier) => (
          <Select.Option
            key={supplier.id}
            value={supplier.id}
            label={supplier.name}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <UserOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                <div>
                  <div>{supplier.name}</div>
                  <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    {supplier.email} • {supplier.country}, {supplier.city}
                  </div>
                </div>
              </div>
              <Tooltip
                title={t("bookingWizard.supplierDetails.deleteSupplier")}
              >
                <DeleteOutlined
                  style={{ color: "#ff4d4f" }}
                  onClick={(e) => handleDeleteSupplier(supplier, e)}
                />
              </Tooltip>
            </div>
          </Select.Option>
        ))}
      </Select>

      {isExwIncoterm && (
        <Alert
          message={t("bookingWizard.transportDetails.exwIncotermSelected")}
          description={t(
            "bookingWizard.transportDetails.exwIncotermExplanation"
          )}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title={t("bookingWizard.supplierDetails.deleteSupplier")}
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText={t("common.delete")}
        okButtonProps={{ danger: true }}
        cancelText={t("common.cancel")}
      >
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 16 }}
        >
          <ExclamationCircleOutlined
            style={{ color: "#faad14", fontSize: 22, marginRight: 16 }}
          />
          <Typography.Text>
            {t("bookingWizard.supplierDetails.confirmDeleteSupplier")}
          </Typography.Text>
        </div>
        <Typography.Text type="secondary">
          {supplierToDelete?.name}
        </Typography.Text>
      </Modal>

      <Card
        title={t("bookingWizard.supplierDetails.title")}
        style={{ marginBottom: 24 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={handleFormChange}
        >
          <Row gutter={16}>
            {/* Contact Information */}
            <Col span={12}>
              <Typography.Text
                type="secondary"
                style={{ marginBottom: 16, display: "block" }}
              >
                {t("bookingWizard.supplierDetails.title")}
              </Typography.Text>

              <Form.Item
                name="name"
                label={t("bookingWizard.supplierDetails.supplierName")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "bookingWizard.supplierDetails.supplierNameRequired"
                    ),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t("bookingWizard.supplierDetails.supplierName")}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label={t("bookingWizard.supplierDetails.email")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.supplierDetails.emailRequired"),
                  },
                  {
                    type: "email",
                    message: t("bookingWizard.supplierDetails.validEmail"),
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t("bookingWizard.supplierDetails.email")}
                />
              </Form.Item>

              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                <Form.Item
                  name="phone"
                  label={t("bookingWizard.supplierDetails.phone")}
                  rules={[
                    {
                      required: true,
                      message: t("bookingWizard.supplierDetails.phoneRequired"),
                    },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder={t("bookingWizard.supplierDetails.phone")}
                  />
                </Form.Item>

                <Form.Item
                  name="wechat"
                  label={t("bookingWizard.supplierDetails.wechat")}
                >
                  <Input
                    prefix={<WechatOutlined />}
                    placeholder={t("bookingWizard.supplierDetails.wechat")}
                  />
                </Form.Item>
              </Space>
            </Col>

            {/* Address Information */}
            <Col span={12}>
              <Typography.Text
                type="secondary"
                style={{ marginBottom: 16, display: "block" }}
              >
                {t("bookingWizard.supplierDetails.addressInformation")}
              </Typography.Text>

              <Form.Item
                name="country"
                label={t("bookingWizard.supplierDetails.country")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.supplierDetails.countryRequired"),
                  },
                ]}
              >
                <Input
                  prefix={<GlobalOutlined />}
                  placeholder={t("bookingWizard.supplierDetails.country")}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="province"
                    label={t("bookingWizard.supplierDetails.province")}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "bookingWizard.supplierDetails.provinceRequired"
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("bookingWizard.supplierDetails.province")}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="city"
                    label={t("bookingWizard.supplierDetails.city")}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "bookingWizard.supplierDetails.cityRequired"
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("bookingWizard.supplierDetails.city")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="address"
                label={t("bookingWizard.supplierDetails.address")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.supplierDetails.addressRequired"),
                  },
                ]}
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  placeholder={t("bookingWizard.supplierDetails.address")}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Only show checkbox when a company supplier is selected and form has changed */}
          {selectedCompanySupplier && formChanged && (
            <Form.Item
              name="saveForLater"
              valuePropName="checked"
              style={{ marginTop: 16 }}
            >
              <Checkbox>
                {t("bookingWizard.supplierDetails.saveForLater")}
              </Checkbox>
            </Form.Item>
          )}

          {/* Show regular checkbox when no company supplier is selected */}
          {!selectedCompanySupplier && (
            <Form.Item
              name="saveForLater"
              valuePropName="checked"
              style={{ marginTop: 16 }}
            >
              <Checkbox>
                {t("bookingWizard.supplierDetails.saveForLater")}
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </Card>
    </WizardStep>
  );
};
